import React, { useCallback } from 'react';
import { get, noop, isEmpty, includes, defaultTo, isEqual, size, slice, filter, map, times, cloneDeep } from 'lodash';
import classnames from 'classnames';

// local dependencies
import { getLabel } from 'utils/getLabel';
import { DATA_TYPES, BLANK_VALUE_SYMBOL, GLOBAL_LABELS } from 'constants/common';
import { checkbox as CheckBox } from 'ui/views/generic/checkbox';
import { isVisible } from 'resolvers/getVisibleEntities';
import { table as Table } from 'modules/table';
import { Icon } from 'modules/icon-bank';
import { actionList as ActionListView } from 'modules/action-list';

// data cell views
import { simpleSelect as SimpleSelectCellView } from 'modules/table/cellViews/simpleSelect.view';
import { simpleText as SimpleTextCellView } from 'modules/table/cellViews/simpleText.view';
import { deepSelect as DeepSelectCellView } from 'modules/table/cellViews/deepSelect.view';
import { popover } from 'modules/popover';

/******************************** */

const IsFetchingView = () => {

    return (
        <div  className='ui-table-block-row-view-preloader'>
            <div className='ui-table-block-row-view-preloader__body'>
                {
                    times( 1 ).map( rowIndex => {
                        return (
                            <div key={ rowIndex } className='ui-table-block-row-view-preloader__body__row'>
                                {
                                    times( 5 ).map( colIndex => {
                                        return (
                                            <div key={ colIndex } className='ui-table-block-row-view-preloader__body__row__cell'>
                                                <div className='ui-table-block-row-view-preloader__body__row__cell__value' />
                                                <div className='ui-table-block-row-view-preloader__body__row__cell__value' />
                                            </div>
                                        );
                                    } )
                                }
                            </div>
                        );
                    } )
                }
            </div>
            <div className='ui-table-block-row-view-preloader__small-table'>
                <div className='ui-table-block-row-view-preloader__small-table__head'>
                    {
                        times( 7 ).map( colIndex => {
                            return (
                                <div key={ colIndex } className='ui-table-block-row-view-preloader__small-table__head__cell'>
                                    <div className='ui-table-block-row-view-preloader__small-table__head__cell__value' />
                                </div>
                            );
                        } )
                    }
                </div>

                <div className='ui-table-block-row-view-preloader__small-table__body'>
                    {
                        times( 2 ).map( rowIndex => {
                            return (
                                <div key={ rowIndex } className='ui-table-block-row-view-preloader__small-table__body__row'>
                                    {
                                        times( 7 ).map( colIndex => {
                                            return (
                                                <div key={ colIndex } className='ui-table-block-row-view-preloader__small-table__body__row__cell'>
                                                    <div className='ui-table-block-row-view-preloader__small-table__body__row__cell__value' />
                                                    <div className='ui-table-block-row-view-preloader__small-table__body__row__cell__value' />
                                                </div>
                                            );
                                        } )
                                    }
                                </div>
                            );
                        } )
                    }
                </div>
            </div>
        </div>
    );
};

const ActionDropdown = ( props ) => {

    // open popover options
    const openPopover = useCallback( ( element ) => {
        popover.open( {
            referenceElement: element,
            view: ActionListView,
            tooltip: false,
            props: {
                width: props.flyoutWidth,
                onActionClick: props.onActionClick,
                actions: map( props.action.actions, action => {
                    return {
                        ...action,
                        iconName: get( props.layoutConfig, `actionIcons.${ action.name }`, 'click' ),
                    };
                } ),
            }
        } );
    }, [] );

    return (
        <div className={ props.widgetClassName } onClick={ ( event ) => props.isActive ? openPopover( event.currentTarget ): noop }>
            <div className='ui-table__body__data__row__primary__cell__actions__item__icon'>
                <Icon
                    name='more'
                    width={ 16 }
                    height={ 16 }
                    fill={ props.isActive ? THEME.themeColorPrimary2 : '#7a7a7a' }
                />
            </div>

            <div className='ui-table__body__data__row__primary__cell__actions__item__title'>
                { getLabel( props.action.title ) }
            </div>
        </div>
    );
};

// set display name
ActionDropdown.displayName = 'table.ActionDropdown';

/****************************************/

// group action view
export const TableHeadGroupAction = ( props ) => {

    // action item class name
    const widgetClassName = classnames( 'ui-table__body__data__row__primary__cell__actions__item', {
        'ui-table__body__data__row__primary__cell__actions__item--disabled': ! props.isActive,
    } );
    
    // get action icon name
    const iconName = get( props.layoutConfig, `actionIcons.${ props.action.name }`, 'click' );

    // get `hideIfDisabled` from action config
    // const hideIfDisabled = get( props, 'action.config.hideIfDisabled', false );

    // return null if action is not active and `hideIfDisabled`: true
    if( ! props.isActive ) {
        return null;
    }
    
    const actionLabel = getLabel( get( props, 'action.title', {} ) );

    if( 'dropdown' === props.action.type ) {
        return (
            <ActionDropdown
                action={ props.action }
                widgetClassName={ widgetClassName }
                isActive={ props.isActive }
                onActionClick={ props.onClick }
                layoutConfig={ props.layoutConfig }
            />
        );
    }

    return (
        <div className={ widgetClassName } onClick={ props.onClick }>
            <div className='ui-table__body__data__row__primary__cell__actions__item__icon'>
                <Icon
                    name={ iconName }
                    width={ 16 }
                    height={ 16 }
                    fill={ props.isActive ? THEME.themeColorPrimary2 : '#7a7a7a' }
                />
            </div>

            <div className='ui-table__body__data__row__primary__cell__actions__item__title'>
                { actionLabel }
            </div>
        </div>
    );
};

// set display name
TableHeadGroupAction.displayName = 'table.TableHeadGroupAction';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { secondaryTable as SecondaryTable } from 'ui/views/widgets/secondaryTable';
 *
 * // render
 * <SecondaryTable propName='propValue'/>
 */
export const tableBlockRow = ( props ) => {

    // table items for the given rowIndex
    const tableItems = props.items[ props.rowIndex ].values.items;

    // check if head action column needs to be displayed
    const headActions = filter( get( props, 'actionsConfig.headActions.actions', [] ), ( headAction ) => {
        return get( props, `item.actionsVisibility.headActions.${ headAction.visibility }`, false );
    } );
    const headVisibleActions = slice( headActions, 0, 3 );
    const headInvisibleActions = slice( headActions, 3 );
    const isHeadActionsColumnVisible = isVisible( get( props, 'actionsConfig.headActions', {} ), props.visibility );
    
    const headActionsColumnWidth = get( props.layoutConfig, 'columnWidth._headActions', 100 );

    // check if selection action column needs to be displayed
    const selectionActions =  get( props, 'actionsConfig.selectionActions.actions', [] );
    const isSelectionActionsVisible = isVisible( get( props, 'actionsConfig.selectionActions', {} ), props.visibility );

    // const headSelectionActions =  get( props, 'actionsConfig.headSelectionActions.actions', [] );
    // const isHeadActionSelectionActionsVisible = isVisible( get( props, 'actionsConfig.headSelectionActions', {} ), props.visibility );

    // to preserve the current row id & if selected or not
    const rowId = get( props, 'item.id', '' );
    const isChecked = includes( props.selectedRows, rowId );
    const statushold = props.item.values.accountStatus === "hold";
    const statusinactive = props.item.values.accountStatus === "inactive";
    const holdinactiveClassName = classnames( 'ui-table-block-row-view__selection__head__cell', {
        'ui-table-block-row-viewhold__selectionhold__headhold__cellhold': statushold,
        'ui-table-block-rowinactive-viewinactive__selectioninactive__headinactive__cellinactive': statusinactive,
    } );
    const holdinactiveClassName1 = classnames( 'ui-table-block-row-view__selection__head__cell__title', {
        'ui-table-block-row-viewhold__selectionhold__headhold__cellhold__titlehold': statushold,
        'ui-table-block-rowinactive-viewinactive__selectioninactive__headinactive__cellinactive__titleinactive': statusinactive,
    } );

    const holdinactiveClassName2 = classnames( 'ui-table-block-row-view__selection__head__cell__values', {
        'ui-table-block-row-viewhold__selectionhold__headhold__cellhold__valueshold': statushold,
        'ui-table-block-rowinactive-viewinactive__selectioninactive__headinactive__cellinactive__valuesinactive': statusinactive,
    } );

    const holdinactiveClassName3 = classnames( 'ui-table-block-row-view', {
        'ui-table-block-row-viewhold': statushold,
        'ui-table-block-rowinactive-viewinactive': statusinactive,
    } );

    const holdinactiveClassName4 = classnames( 'ui-table-block-row-view__selection', {
        'ui-table-block-row-viewhold__selectionhold': statushold,
        'ui-table-block-rowinactive-viewinactive__selectioninactive': statusinactive,
    } );

    const holdinactiveClassName5 = classnames( 'ui-table-block-row-view__selection__head', {
        'ui-table-block-row-viewhold__selectionhold__headhold': statushold,
        'ui-table-block-rowinactive-viewinactive__selectioninactive__headinactive': statusinactive,
    } );

   
    // status of checkbox
    let checkVal = false;

    if( 0 < size( props.selectedTableRows ) ) {
        checkVal = isEqual( props.selectedTableRows.length, tableItems.length ) ? true: undefined;
    }

    // prepare a new actions config for child table
    const newActionsConfig = cloneDeep( props.actionsConfig );
    newActionsConfig[ 'selectionActions' ] = {};
    
    return (

        ! props.item.fetching ? (
            <div className={ holdinactiveClassName3 }>

                <div className={ holdinactiveClassName4 }>

                    <div className={ holdinactiveClassName5 }>

                        {/* selection actions cells */}
                        {
                            ! isEmpty( selectionActions ) && isSelectionActionsVisible ? (
                                <div className='ui-table-block-row-view__selection__head__checkbox'>
                                    <div className='ui-table-block-row-view__selection__head__checkbox__icon'>
                                        {
                                            selectionActions.map( ( selectionAction ) => {
                                                return (
                                                    <div id={ rowId } key={ rowId } >
                                                        <CheckBox
                                                            key={ selectionAction.name }
                                                            size='xs'
                                                            value={ checkVal }
                                                            id={ rowId }
                                                            onClick={ () => props.onSelect( { item: rowId, isChecked: ! isChecked } ) }
                                                        />
                                                    </div>
                                                );
                                            } )
                                        }
                                    </div>
                                </div>
                            ) : null
                        }

                        {/* regular cells */}
                        {
                            props.columns.head.map( column => {

                                // get column width from the `layoutConfig` (default: 100px)
                                const columnWidth = get( props.layoutConfig, `columnWidth.${ column.name }`, 100 );

                                // check column is included in wildcard search
                                const isSearchableColumn = includes( props.searchableColumns, column.name );

                                // column value
                                const value = get( props, `item.values.${ column.name }`, '' );

                                // check if column is actionable
                                const actionConfig = defaultTo( column.actionConfig, {} );

                                const isActionActive = ! isEmpty( actionConfig ) && get( props, `item.actionsVisibility.columnActions.${ actionConfig.visibility }`, true );

                                // check if info icon and tooltip is to be shown
                                const uiConfig = defaultTo( column.uiConfig, {} );
                                const isInfoVisible = ! isEmpty( uiConfig ) && get( props, `item.iconsVisibility.${ uiConfig.info.visibility }`, false );

                                // set column icon and tooltip message
                                let icon, tooltipMessage = null;
                                if ( isInfoVisible ) {
                                    icon = get( props.layoutConfig, `actionIcons.${ column.name }`, 'info' );
                                    tooltipMessage = getLabel( get( uiConfig, 'info.message', {} ) );
                                }

                                // get max display values
                                const maxDisplayValues = get( column, 'uiConfig.maxDisplayValues' );

                                /*---------*/

                                    // if value is empty, render blank symbol
                                if( isEmpty( value ) ) {
                                    return (
                                        <div key={ column.name } style={ { width: columnWidth } } className='ui-table-block-row-view__selection__head__cell'>
                                            <div className='ui-table-block-row-view__selection__head__cell__title'>{ getLabel( column.title ) }</div>
                                            { BLANK_VALUE_SYMBOL }
                                        </div>
                                    );
                                }

                                /*---------*/

                                // render column display values for `single-select` and `multi-select` data types
                                if( includes( [ DATA_TYPES.SINGLE_SELECT, DATA_TYPES.MULTI_SELECT ], column.type ) ) {

                                    return (
                                        <div key={ column.name } style={ { width: columnWidth } } className={ holdinactiveClassName }>
                                            
                                            <div className={ holdinactiveClassName1 }>{ getLabel( column.title ) }</div>
                                            
                                            <SimpleSelectCellView
                                                className={ holdinactiveClassName2 }
                                                options={ column.options }
                                                value={ value }
                                                maxDisplayValues={ maxDisplayValues }
                                                displayColors={ get( props.layoutConfig, `valueColors.${ column.name }`, {} ) }
                                                isSearchable={ isSearchableColumn }
                                                searchQuery={ props.searchQuery }
                                                isActionable={ isActionActive }
                                                onClick={ isActionActive ?
                                                    () => props.onActionClick( { type: 'column', value: column, item: props.item } )
                                                : noop }
                                            />
                                        </div>
                                    );
                                }

                                /*----*/

                                // render column display values for `deep-select` and `deep-multi-select` data types
                                if( includes( [ DATA_TYPES.DEEP_SELECT, DATA_TYPES.DEEP_MULTI_SELECT ], column.type ) ) {
                                    return (
                                        <div key={ column.name } style={ { width: columnWidth } } className={ holdinactiveClassName }>
                                            
                                            <div className={ holdinactiveClassName1 }>{ getLabel( column.title ) }</div>
                                            
                                            <DeepSelectCellView
                                                className={ holdinactiveClassName2 }
                                                options={ column.options }
                                                value={ value }
                                                displayColors={ get( props.layoutConfig, `valueColors.${ column.name }`, {} ) }
                                                isSearchable={ isSearchableColumn }
                                                searchQuery={ props.searchQuery }
                                            />
                                        </div>
                                    );
                                }
                           
                                /*----*/

                                // render column display values for other` data types
                                return (
                                    <div key={ column.name } style={ { width: columnWidth } } className={ holdinactiveClassName }>
                                        <div className={ holdinactiveClassName1 }>{ getLabel( column.title ) }</div>
                                        <SimpleTextCellView
                                            className={ holdinactiveClassName2 }
                                            value={ value }
                                            isSearchable={ isSearchableColumn }
                                            searchQuery={ props.searchQuery }
                                            maxDisplayValues={ maxDisplayValues }
                                            isActionable={ isActionActive }
                                            onClick={ isActionActive ? () => props.onActionClick( { type: 'column', value: column, item: props.item } ) : noop }
                                            columnType={ column.type }
                                            icon={ icon }
                                            tooltipMessage={ tooltipMessage }
                                            itemId={ get( props, 'item.id', null ) }  // to ensure there are no `id` conflicts in case of multiple tooltips on page
                                            {...props}
                                        />
                                    </div>
                                );

                                
                            } )
                        }

                        {/* group actions cells */}
                        {
                            ( isHeadActionsColumnVisible && ! isEmpty( headVisibleActions ) ) ? (
                                <div style={ { width: headActionsColumnWidth } } className={ holdinactiveClassName }>
                                    <div className='ui-table__body__data__row__primary__cell__actions'>
                                        {
                                            headVisibleActions.map( headAction => {

                                                // check if action is active
                                                const isActionActive = get( props, `item.actionsVisibility.headActions.${ headAction.visibility }`, false );

                                                // to determine the action associated with `headAction` is selection or not.
                                                const isSelection = get( headAction, 'config.isSelection', false );

                                                return (
                                                    <TableHeadGroupAction
                                                        key={ headAction.name }
                                                        action={ headAction }
                                                        isActive={ isActionActive }
                                                        layoutConfig={ props.layoutConfig }
                                                        onClick = { () => {
                                                            if( isActionActive ) {

                                                                // this enables the selection on click of the action.
                                                                if( isSelection ) {

                                                                    props.onSelect(  { item: rowId, isChecked: ! isChecked } );
                                                                    
                                                                } else {

                                                                    props.onActionClick( { type: 'headAction', value: headAction, item: props.item } );
                                                                }

                                                            } else {
                                                                noop;
                                                            }
                                                        } }

                                                        { ...props }
                                                    />
                                                );
                                            } )
                                        }

                                        {/* more action */}
                                        {
                                            isEmpty( headInvisibleActions ) ? null : (
                                                <TableHeadGroupAction
                                                    layoutConfig={ props.layoutConfig }
                                                    isActive={ true }
                                                    action={ {
                                                        name: 'more',
                                                        type: 'dropdown',
                                                        title: GLOBAL_LABELS.DISPLAY_TEXT_MORE,
                                                        actions: headInvisibleActions,
                                                    } }
                                                    onClick = { ( _action ) => {
                                                        props.onActionClick( { type: 'headAction', value: _action, item: props.item } );
                                                    } }
                                                />
                                            )
                                        }
                                    </div>
                                </div>
                            ) : null
                        }
                    </div>

                </div>

            {/* default table view */}
                <Table
                    items={ tableItems }
                    actionsConfig={ newActionsConfig }
                    layoutConfig={ props.layoutConfig }
                    columns={ props.columns }
                    searchableColumns={ props.searchableColumns }
                    searchQuery={ props.searchQuery }
                    selectedRows={ props.selectedTableRows }
                    visibility={ props.visibility }

                    onSelect={ props.onRowSelection }
                    onActionClick={ props.onRowActionClick }
                    
                    disablePagination={ true }
                    disbleTableSelectionView={ true }
                />
            </div>
        ) : <IsFetchingView />
    );
};

// set display name
tableBlockRow.displayName = 'tableBlockRow';

// set default props
tableBlockRow.defaultProps = {};

// prop types
tableBlockRow.propTypes = {};
